import {
  FC,
  RefObject,
  ReactNode,
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  createContext,
} from 'react';

type ScrollContainerRef = RefObject<HTMLDivElement | null>;

type SyncScrollContextValue = {
  maxScrollWidth: number;
  registerScrollContainer: (ref: ScrollContainerRef) => () => void;
  syncScroll: (
    scrollLeft: number,
    sourceRef: ScrollContainerRef | null,
  ) => void;
};

const getMaxScrollWidth = (containers: Set<ScrollContainerRef>) => {
  let max = 0;
  containers.forEach((ref) => {
    if (ref.current?.querySelector('table') != null) {
      max = Math.max(max, ref.current.scrollWidth);
    }
  });
  return max;
};

const SyncScrollContext = createContext<SyncScrollContextValue | null>(null);

export const SyncScrollProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [maxScrollWidth, setMaxScrollWidth] = useState(0);
  const containersRef = useRef<Set<ScrollContainerRef>>(new Set());
  const observersRef = useRef<Map<ScrollContainerRef, ResizeObserver>>(
    new Map(),
  );

  const registerScrollContainer = useCallback((ref: ScrollContainerRef) => {
    containersRef.current.add(ref);
    setMaxScrollWidth(getMaxScrollWidth(containersRef.current));

    if (ref.current != null) {
      const observer = new ResizeObserver(() => {
        setMaxScrollWidth(getMaxScrollWidth(containersRef.current));
      });
      ref.current
        .querySelectorAll('table')
        .forEach((table) => observer.observe(table));
      Array.from(ref.current.children).forEach((child) =>
        observer.observe(child),
      );
      observersRef.current.set(ref, observer);
    }

    return () => {
      const observer = observersRef.current.get(ref);
      if (observer != null) {
        observer.disconnect();
        observersRef.current.delete(ref);
      }
      containersRef.current.delete(ref);
      setMaxScrollWidth(getMaxScrollWidth(containersRef.current));
    };
  }, []);

  useEffect(() => {
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current.clear();
    };
  }, []);

  const syncScroll = (
    scrollLeft: number,
    sourceRef: ScrollContainerRef | null,
  ) => {
    containersRef.current.forEach((ref) => {
      if (ref !== sourceRef && ref.current != null) {
        ref.current.scrollLeft = scrollLeft;
      }
    });
  };

  return (
    <SyncScrollContext.Provider
      value={{ maxScrollWidth, registerScrollContainer, syncScroll }}
    >
      {children}
    </SyncScrollContext.Provider>
  );
};

export const useSyncScroll = (): SyncScrollContextValue => {
  const value = useContext(SyncScrollContext);
  if (value == null) {
    throw new Error('useSyncScroll must be used within SyncScrollProvider');
  }
  return value;
};
